import { Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/* ────────────────────────── Types ────────────────────────────────── */

export interface ChildItem {
	id: string
	name: string
	/** Texto secundário exibido abaixo do nome (ex.: descrição do tema) */
	description?: string
	/** Substitui o índice (1, 2, 3…) exibido no badge do filho */
	badgeLabel?: string
}

export interface GroupItem {
	id: string
	name: string
	score?: number | null
	/** Flag do Sphera configurada no cadastro (ex.: Safety:Working at height) */
	spheraName?: string | null
	children: ChildItem[]
}

interface GroupedListProps {
	groups: GroupItem[]
	/** Current search string — matched groups auto-expand */
	search?: string
	/**
	 * Full Tailwind color class applied to the count badge background,
	 * e.g. "bg-kaefer-medium-grey/15 text-kaefer-medium-grey"
	 */
	badgeClass?: string
	/** Full Tailwind color class for the child index badge */
	childBadgeClass?: string
	/** Omitir para esconder o botão de editar do grupo */
	onEditGroup?: (group: GroupItem) => void
	/** Omitir para esconder o botão de excluir do grupo */
	onDeleteGroup?: (id: string, name: string) => void
	/** Quando informado, cada filho ganha um botão de editar */
	onEditChild?: (child: ChildItem, group: GroupItem) => void
	/** Shown when groups list is empty */
	emptyMessage?: string
	/** Optional icon to show when list is empty */
	emptyIcon?: React.ReactNode
	/** Label for the "add child" button inside edit dialog — not used internally, but forwarded */
	addChildLabel?: string
}

/* ────────────────────────── Component ───────────────────────────── */

export function GroupedList({
	groups,
	search = '',
	badgeClass = 'bg-kaefer-light-silver/70 text-kaefer-dark-grey',
	childBadgeClass = 'bg-kaefer-medium-grey/15 text-kaefer-medium-grey',
	onEditGroup,
	onDeleteGroup,
	onEditChild,
	emptyMessage = 'Nenhum item encontrado',
	emptyIcon,
}: GroupedListProps) {
	const { t } = useTranslation()
	const q = search.trim().toLowerCase()

	const childMatches = (child: ChildItem) =>
		`${child.name} ${child.description ?? ''}`.toLowerCase().includes(q)

	// Determine which accordion items should be forced open due to search
	const autoOpenValues = q
		? groups
				.filter(
					(g) =>
						g.name.toLowerCase().includes(q) || g.children.some(childMatches),
				)
				.map((g) => g.id)
		: []

	if (groups.length === 0) {
		return (
			<div className="flex flex-col items-center gap-2 py-16 text-center text-muted-foreground">
				{emptyIcon}
				<p className="text-sm">{emptyMessage}</p>
			</div>
		)
	}

	return (
		<Accordion
			type="multiple"
			// When searching, force matching items open; otherwise let user control
			value={q ? autoOpenValues : undefined}
			className="space-y-2"
		>
			{groups.map((group) => {
				const visibleChildren =
					q && !group.name.toLowerCase().includes(q)
						? group.children.filter(childMatches)
						: group.children

				return (
					<AccordionItem
						key={group.id}
						value={group.id}
						className="relative overflow-hidden rounded-lg border border-kaefer-light-silver bg-white last:border-b"
					>
						{/* ── Group row ── */}
						<div className="relative px-4 py-3">
							<AccordionTrigger className="flex-1 w-full flex items-center justify-between hover:no-underline gap-3 pr-24">
								<div className="flex items-center gap-2.5">
									<span className=" text-kaefer-dark-grey">{group.name}</span>
									{group.spheraName && (
										<span className="hidden text-xs text-muted-foreground sm:inline">
											{group.spheraName}
										</span>
									)}
									<span
										className={cn(
											'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] tabular-nums',
											badgeClass,
										)}
									>
										{group.children.length}
									</span>
									{group.score != null && (
										<span className="text-xs tabular-nums text-muted-foreground">
											{t('common.score')}: {group.score}
										</span>
									)}
								</div>
							</AccordionTrigger>

							{/* buttons absolutely positioned to the right edge of wrapper */}
							<div className="absolute inset-y-0 right-4 flex items-center gap-1">
								{onEditGroup && (
									<Button
										type="button"
										variant="ghost"
										size="icon-circle"
										onClick={(e) => {
											e.stopPropagation()
											onEditGroup(group)
										}}
										className="text-kaefer-dark-grey hover:bg-transparent hover:text-kaefer-dark-grey/70"
									>
										<Pencil />
									</Button>
								)}
								{onDeleteGroup && (
									<Button
										type="button"
										variant="ghost"
										size="icon-circle"
										onClick={(e) => {
											e.stopPropagation()
											onDeleteGroup(group.id, group.name)
										}}
										className="text-kaefer-red hover:bg-transparent hover:text-kaefer-red/70"
									>
										<Trash2 />
									</Button>
								)}
							</div>
						</div>

						{/* ── Children ── */}
						<AccordionContent className="pb-0">
							<div className="border-t border-kaefer-light-silver bg-muted/30">
								{visibleChildren.length === 0 ? (
									<p className="px-4 py-3 text-xs italic text-muted-foreground">
										Nenhum item
									</p>
								) : (
									visibleChildren.map((child, idx, arr) => (
										<div
											key={child.id}
											className={cn(
												'flex items-center justify-between px-4 py-3 text-sm', // py-3 para melhor clique no filho
												idx < arr.length - 1 &&
													'border-b border-kaefer-light-silver/60',
											)}
										>
											<div className="flex min-w-0 items-center gap-3">
												<span
													className={cn(
														'flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
														childBadgeClass,
													)}
												>
													{child.badgeLabel ?? idx + 1}
												</span>
												<div className="min-w-0">
													<span className="text-kaefer-dark-grey">
														{child.name}
													</span>
													{child.description && (
														<p className="text-xs text-muted-foreground">
															{child.description}
														</p>
													)}
												</div>
											</div>

											{onEditChild && (
												<Button
													type="button"
													variant="ghost"
													size="icon-circle"
													onClick={() => onEditChild(child, group)}
													className="shrink-0 text-kaefer-dark-grey hover:bg-transparent hover:text-kaefer-dark-grey/70"
												>
													<Pencil />
												</Button>
											)}
										</div>
									))
								)}
							</div>
						</AccordionContent>
					</AccordionItem>
				)
			})}
		</Accordion>
	)
}
